// R17 Wave 0 extraction — pure refactor, no behavior change
import React from 'react';
import { Printer, ChevronDown, ChevronUp, ArrowRight, GitBranch } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import type { BlotterReportData, UserReportManualCheck } from '../../types/report';
import BlotterEmployeeChecks from './BlotterEmployeeChecks';

/**
 * Local Employee shape used by the Blotter Report results view. Diverges
 * slightly from the shared `BlotterReportEmployee` type (adds `is_house`,
 * `user_commission_id` on transactions) so keep it colocated with the
 * consumer. Exported so the parent can also strongly type its sorted array.
 */
export interface BlotterEmployee {
  user_id: number;
  user_name: string;
  user_email: string;
  is_house?: boolean;
  total_gross_commission: number;
  total_commission: number;
  transaction_count: number;
  // R18 Task A: the blotter now folds in the day's manual checks, so the
  // gross/net above are trade + check combined. These break the two sides
  // apart and carry the checks themselves for the detail view (Task B).
  trade_gross_commission?: number;
  trade_user_commission?: number;
  check_gross_commission?: number;
  check_user_commission?: number;
  check_count?: number;
  manual_checks?: UserReportManualCheck[];
  transactions: Array<{
    id: number;
    date: string;
    account_id: number;
    account_name: string;
    client_name: string;
    security: string;
    bought: number;
    sold: number;
    price: number;
    rate: number;
    gross_commission: number;
    employee_percentage: number;
    employee_commission: number;
    user_commission_id: number;
  }>;
  /**
   * R17 Task C: `true` when this employee row was synthesized from a
   * manual-check-only day (or a salesman with checks on a date they had
   * no trades). Drives the "Checks only" badge + the "no trades to
   * display" hint in the expanded transactions table. The Print PDF
   * button still hits the same consolidated PDF endpoint, which
   * gracefully handles zero-transaction days per R16 Task A.
   */
  checks_only?: boolean;
}

export type OpenCommissionModal = (
  mode: 'move' | 'split',
  transactionId: number,
  grossCommission: number,
  currentUserId?: number,
  currentUserName?: string,
  currentPercentage?: number
) => void;

interface BlotterReportResultsProps {
  reportData: BlotterReportData;
  employees: BlotterEmployee[];
  isAdmin: boolean;
  expandedEmployees: Set<number>;
  onToggleEmployee: (userId: number) => void;
  onPrintEmployee: (employee: BlotterEmployee) => void;
  printingEmployee: number | null;
  onOpenCommissionModal: OpenCommissionModal;
  onDownloadBlotterFile: () => void;
}

/**
 * Summary + employee-cards block for the Blotter Report page. Extracted from
 * BlotterReport.tsx in R17 Wave 0. R17 Task C will layer in synthetic
 * "check-only" employee rows here (when a salesman has a manual check on a
 * date with no blotter row); keeping the results in a dedicated file makes
 * that diff reviewable in isolation.
 */
const BlotterReportResults: React.FC<BlotterReportResultsProps> = ({
  reportData,
  employees,
  isAdmin,
  expandedEmployees,
  onToggleEmployee,
  onPrintEmployee,
  printingEmployee,
  onOpenCommissionModal,
  onDownloadBlotterFile,
}) => {
  // R17 Task C: when no CSV blotter exists for the selected date but
  // manual checks do, the parent synthesizes a shell with `blotter.id = 0`.
  // Surface a small hint so Peter sees why the trade totals are zero.
  const isCheckOnlyDay = reportData.blotter.id === 0;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Summary</h2>
        {isCheckOnlyDay && (
          <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700">
            <span className="font-medium">Checks-only day.</span> No CSV blotter
            was uploaded for this date, but salesmen have manual checks on file.
            Use the Print PDF button on each employee row below to download
            their daily commission PDF.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Date</p>
            <p className="text-lg font-semibold text-gray-900">{formatDate(reportData.blotter.date)}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Gross Commission</p>
            <p className="text-lg font-semibold text-green-700">
              {formatCurrency(reportData.summary.gross_commission)}
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Salesman Commission</p>
            <p className="text-lg font-semibold text-blue-700">
              {formatCurrency(reportData.summary.salesman_commission ?? reportData.summary.user_commission ?? 0)}
            </p>
          </div>
          <div className="bg-amber-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">House Commission</p>
            <p className="text-lg font-semibold text-amber-700">
              {formatCurrency(reportData.summary.house_commission ?? reportData.summary.net_commission ?? 0)}
            </p>
          </div>
          <div className="bg-primary-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Total Transactions</p>
            <p className="text-lg font-semibold text-primary-700">
              {reportData.summary.transaction_count}
            </p>
          </div>
        </div>

        {/* R18 hotfix: trades and checks broken out. Gross Commission above
            now covers both, which left nothing to reconcile against the old
            system -- that system knows nothing about checks. The Trades row
            matches the old blotter total exactly. Labels are Peter's own. */}
        {reportData.summary.trades_total !== undefined && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <table className="w-full max-w-2xl text-sm">
              <tbody>
                <tr>
                  <td className="py-1 text-gray-600">Trades Total:</td>
                  <td className="py-1 text-right font-semibold text-gray-900">
                    {formatCurrency(reportData.summary.trades_total)}
                  </td>
                  <td className="py-1 pl-8 text-gray-600">Trades Net Total:</td>
                  <td className="py-1 text-right font-semibold text-gray-900">
                    {formatCurrency(reportData.summary.trades_net_total)}
                  </td>
                </tr>
                <tr>
                  <td className="py-1 text-gray-600">Checks Total:</td>
                  <td className="py-1 text-right font-semibold text-gray-900">
                    {formatCurrency(reportData.summary.checks_total)}
                  </td>
                  <td className="py-1 pl-8 text-gray-600">Checks Net Total:</td>
                  <td className="py-1 text-right font-semibold text-gray-900">
                    {formatCurrency(reportData.summary.checks_net_total)}
                  </td>
                </tr>
                <tr className="border-t border-gray-200">
                  <td className="pt-2 font-medium text-gray-700">Total Commission:</td>
                  <td className="pt-2 text-right font-bold text-green-700">
                    {formatCurrency(reportData.summary.gross_commission)}
                  </td>
                  <td className="pt-2 pl-8 font-medium text-gray-700">Total Net Commission:</td>
                  <td className="pt-2 text-right font-bold text-blue-700">
                    {formatCurrency(
                      reportData.summary.salesman_commission ?? reportData.summary.user_commission ?? 0
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            <span className="font-medium">File:</span>{' '}
            {reportData.blotter.filename ? (
              /* The download endpoint is behind auth:sanctum, so we can't
                 use a plain <a href>. Route through apiService so the
                 bearer token is attached and the response is streamed to
                 a blob URL. */
              <button
                type="button"
                onClick={onDownloadBlotterFile}
                className="text-primary-600 hover:text-primary-800 underline cursor-pointer"
              >
                {reportData.blotter.filename}
              </button>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </p>
        </div>
      </div>

      {/* Employees Section Title */}
      {employees.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Employees ({employees.length})
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Click on an employee to see their transactions. Use the Print button to generate a PDF report for that employee.
          </p>
          {isAdmin && (
            <p className="text-sm text-blue-600 mt-2 font-medium">
              Admin: Expand employee sections to see Move/Split commission buttons on each transaction.
            </p>
          )}
        </div>
      )}

      {/* Employee Cards */}
      {employees.map((employee) => {
        const isExpanded = expandedEmployees.has(employee.user_id);
        const isPrinting = printingEmployee === employee.user_id;
        const isHouse = !!employee.is_house;
        // R17 Task C: check-only rows visually distinguish themselves with
        // a slate border + badge so Peter sees at a glance which entries
        // came from manual checks rather than a real trading blotter.
        const isChecksOnly = !!employee.checks_only;

        return (
          <div
            key={employee.user_id}
            className={`bg-white rounded-lg shadow-sm overflow-hidden ${
              isHouse
                ? 'border-2 border-amber-300'
                : isChecksOnly
                  ? 'border-2 border-slate-300 border-dashed'
                  : 'border border-gray-200'
            }`}
          >
            {/* Employee Header */}
            <div
              className={`px-6 py-4 flex items-center justify-between ${
                isHouse ? 'bg-amber-50' : isChecksOnly ? 'bg-slate-50' : 'bg-gray-50'
              }`}
            >
              <button
                onClick={() => onToggleEmployee(employee.user_id)}
                className={`flex-1 flex items-center text-left transition-colors -m-4 p-4 rounded-lg ${
                  isHouse
                    ? 'hover:bg-amber-100'
                    : isChecksOnly
                      ? 'hover:bg-slate-100'
                      : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center">
                    {isHouse && (
                      <span className="mr-2 px-2 py-0.5 text-xs font-medium bg-amber-200 text-amber-900 rounded">
                        HOUSE
                      </span>
                    )}
                    {isChecksOnly && !isHouse && (
                      <span
                        className="mr-2 px-2 py-0.5 text-xs font-medium bg-slate-200 text-slate-800 rounded"
                        title="This day has no CSV blotter for this employee — a Print PDF is still available because they have manual checks on file for this date."
                      >
                        CHECKS ONLY
                      </span>
                    )}
                    {employee.user_name}
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 ml-2 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 ml-2 text-gray-400" />
                    )}
                  </h3>
                  {employee.user_email && (
                    <p className="text-xs text-gray-500 mt-1">{employee.user_email}</p>
                  )}
                  <div className="mt-2 flex items-center space-x-6">
                    <div>
                      <span className="text-xs text-gray-500">Gross Commission: </span>
                      <span className="text-sm font-semibold text-blue-700">
                        {formatCurrency(employee.total_gross_commission)}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">{isHouse ? 'House Keeps' : 'Salesman Payout'}: </span>
                      <span className={`text-sm font-semibold ${isHouse ? 'text-amber-700' : 'text-green-700'}`}>
                        {formatCurrency(employee.total_commission)}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Transactions: </span>
                      <span className="text-sm font-medium text-gray-900">
                        {employee.transaction_count}
                      </span>
                    </div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => onPrintEmployee(employee)}
                disabled={isPrinting}
                className="ml-4 flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                <Printer className="h-4 w-4 mr-2" />
                {isPrinting ? 'Printing...' : 'Print PDF'}
              </button>
            </div>

            {/* R17 Task C: no trades on this date — the check payout data
                lives on the salesman's daily PDF (which the Print button
                above generates). We don't inline the check rows here;
                Peter reviews check math on the Manual Checks screen. */}
            {isExpanded && isChecksOnly && (
              <div className="border-t border-gray-200 p-6 text-sm text-slate-600 bg-slate-50">
                No CSV trades on this date. Manual check payouts are included
                in the Print PDF. Enter or review checks on the
                <span className="font-medium"> Manual Checks </span> screen.
              </div>
            )}

            {/* Transactions Table */}
            {isExpanded && !isChecksOnly && (
              <div className="border-t border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Bought</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sold</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Security</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Comm.</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Comm. %</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">User Comm.</th>
                        {isAdmin && (
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {employee.transactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap">{formatDate(transaction.date)}</td>
                          <td className="px-3 py-3 text-sm text-right text-gray-900">{transaction.bought > 0 ? transaction.bought.toLocaleString() : ''}</td>
                          <td className="px-3 py-3 text-sm text-right text-gray-900">{transaction.sold > 0 ? transaction.sold.toLocaleString() : ''}</td>
                          <td className="px-3 py-3 text-sm text-gray-900">{transaction.security}</td>
                          <td className="px-3 py-3 text-sm text-right text-gray-900">{formatCurrency(transaction.price)}</td>
                          <td className="px-3 py-3 text-sm text-gray-900">{transaction.account_name}</td>
                          <td className="px-3 py-3 text-sm text-right text-gray-600">{transaction.rate.toFixed(4)}</td>
                          <td className="px-3 py-3 text-sm text-right font-medium text-blue-700">{formatCurrency(transaction.gross_commission)}</td>
                          <td className="px-3 py-3 text-sm text-right text-gray-600">{transaction.employee_percentage.toFixed(2)}%</td>
                          <td className="px-3 py-3 text-sm text-right font-semibold text-green-700">{formatCurrency(transaction.employee_commission)}</td>
                          {isAdmin && (
                            <td className="px-4 py-3 text-sm text-center">
                              <div className="flex items-center justify-center space-x-2">
                                <button
                                  onClick={() => onOpenCommissionModal(
                                    'move',
                                    transaction.id,
                                    transaction.gross_commission,
                                    employee.user_id,
                                    employee.user_name,
                                    transaction.employee_percentage
                                  )}
                                  className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center"
                                  title="Move commission to another employee"
                                >
                                  <ArrowRight className="w-3 h-3 mr-1" />
                                  Move
                                </button>
                                <span className="text-gray-300">|</span>
                                <button
                                  onClick={() => onOpenCommissionModal(
                                    'split',
                                    transaction.id,
                                    transaction.gross_commission,
                                    employee.user_id,
                                    employee.user_name,
                                    transaction.employee_percentage
                                  )}
                                  className="text-green-600 hover:text-green-800 text-xs font-medium flex items-center"
                                  title="Split commission with another employee"
                                >
                                  <GitBranch className="w-3 h-3 mr-1" />
                                  Split
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={7} className="px-3 py-3 text-sm text-right font-medium text-gray-700">Total:</td>
                        <td className="px-3 py-3 text-sm text-right font-bold text-blue-700">{formatCurrency(employee.total_gross_commission)}</td>
                        <td className="px-3 py-3"></td>
                        <td className="px-3 py-3 text-sm text-right font-bold text-green-700">{formatCurrency(employee.total_commission)}</td>
                        {isAdmin && <td className="px-3 py-3"></td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* R18 Task B: the day's checks, previously PDF-only. */}
                <BlotterEmployeeChecks checks={employee.manual_checks} />
              </div>
            )}
          </div>
        );
      })}

      {employees.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500">No employees found for this blotter date.</p>
        </div>
      )}
    </div>
  );
};

export default BlotterReportResults;
